import { useEffect, useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Bot,
  CalendarDays,
  ExternalLink,
  Gavel,
  Landmark,
  LoaderCircle,
  Scale,
  UserRound,
} from "lucide-react";
import { FeedbackBanner, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  CopyCanonicalLink,
  formatLegalDate,
  navigateBackToCatalog,
  preservedCatalogHistoryState,
} from "../components/LegalCatalogUi.jsx";
import { usePageMetadata } from "../lib/use-page-metadata.js";

function openJustiniano(prompt) {
  window.dispatchEvent(new CustomEvent("justiniano:compose", {
    detail: { appId: "instrumenta", prompt },
  }));
}

function judgeName(judge) {
  if (typeof judge === "string") return judge;
  return judge?.name || judge?.label || "";
}

export function JurisprudenceDetailPage({ slug }) {
  const api = useApi();
  const { navigate } = useRouter();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.getJurisprudence(slug)
      .then((data) => {
        if (!active) return;
        setRecord(data);
        if (data.slug !== slug) {
          navigate(data.url, {
            replace: true,
            state: preservedCatalogHistoryState(),
          });
        }
      })
      .catch((requestError) => {
        if (!active) return;
        setRecord(null);
        setError(requestError.message);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, navigate, slug]);

  usePageMetadata({
    title: record ? `${record.caseName} · Jurisprudencia · Instrumenta` : null,
    description: record
      ? `${record.court}. ${formatLegalDate(record.date)}. ${record.decisionType}.`
      : null,
    canonicalPath: record?.url,
  });

  if (loading) {
    return (
      <div className="page legal-resource-page">
        <section className="legal-resource-loading">
          <LoaderCircle className="spin" size={23} /> Abriendo el fallo…
        </section>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="page legal-resource-page">
        <button
          className="back-link"
          type="button"
          onClick={() => navigateBackToCatalog(navigate, "/jurisprudencia")}
        >
          <ArrowLeft size={17} /> Volver al buscador
        </button>
        <FeedbackBanner>{error || "No encontramos el fallo solicitado."}</FeedbackBanner>
      </div>
    );
  }

  const judges = record.judges.map(judgeName).filter(Boolean);
  return (
    <div className="page legal-resource-page">
      <button
        className="back-link"
        type="button"
        onClick={() => navigateBackToCatalog(navigate, "/jurisprudencia")}
      >
        <ArrowLeft size={17} /> Volver al buscador de jurisprudencia
      </button>

      <article className="legal-resource-header">
        <div className="legal-resource-heading-icon"><Gavel size={25} /></div>
        <div className="legal-resource-heading-copy">
          <div className="legal-resource-kicker">
            <span className="eyebrow">Jurisprudencia · {record.decisionType}</span>
            {record.official && <span className="official-source"><BadgeCheck size={12} /> Fuente oficial</span>}
          </div>
          <h1>{record.caseName}</h1>
          <div className="legal-resource-meta">
            <span><Landmark size={15} /> {record.court}</span>
            <span><CalendarDays size={15} /> {formatLegalDate(record.date)}</span>
            {record.cuij && <span><Scale size={15} /> CUIJ {record.cuij}</span>}
            {!record.cuij && record.docketNumber && <span><Scale size={15} /> Expte. {record.docketNumber}</span>}
          </div>
        </div>
        <div className="legal-resource-actions">
          <CopyCanonicalLink path={record.url} />
          {record.sourceUrl && (
            <a className="secondary-button" href={record.sourceUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={16} /> Fuente original externa
            </a>
          )}
          <button
            className="primary-button"
            type="button"
            onClick={() => openJustiniano(
              `Analizá este fallo: hechos relevantes, problema jurídico, criterio, fundamentos y decisión. Citá el texto utilizado. ${record.citation}. URL en Instrumenta: ${new URL(record.url, window.location.origin)}`,
            )}
          >
            <Bot size={16} /> Analizar con Justiniano
          </button>
        </div>
      </article>

      {record.summary && <p className="legal-resource-lead">{record.summary}</p>}

      <section className="legal-resource-summary-grid">
        <div><span>Tribunal</span><strong>{record.courtName || record.court}</strong></div>
        <div><span>Fecha</span><strong>{formatLegalDate(record.date)}</strong></div>
        <div><span>Identificador</span><strong>{record.cuij || record.docketNumber || "No informado"}</strong></div>
      </section>

      {(judges.length > 0 || record.matters.length > 0 || record.keywords.length > 0) && (
        <section className="legal-resource-tags">
          {judges.length > 0 && (
            <div><span><UserRound size={14} /> Firmantes</span><p>{judges.join(" · ")}</p></div>
          )}
          {record.matters.length > 0 && (
            <div><span>Materias</span><p>{record.matters.join(" · ")}</p></div>
          )}
          {record.keywords.length > 0 && (
            <div><span>Palabras clave</span><p>{record.keywords.join(" · ")}</p></div>
          )}
        </section>
      )}

      <section className="legal-resource-content">
        <header>
          <div>
            <span className="eyebrow">Texto completo</span>
            <h2>Sentencia</h2>
          </div>
          <span>{record.sourceOrganization || record.sourceTitle}</span>
        </header>
        <article className="legal-decision-text">{record.fullText}</article>
      </section>
    </div>
  );
}
